import { call, all } from 'typed-redux-saga';
import {
  AsyncActionState,
  asyncActionStateMatchers,
  setupSlice,
  invoke,
  SagaGenerator,
} from '@owl-frontend/redux';
import type { User, UserAdmin } from '../../api/interface';
import usersApiClient from '../../api/users/client';
import type { UserForm } from '../../components/AdminPanelContainer/UsersContainer/NewUserModal';

export function normalizeName(name: string): string {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return '';
  }

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function normalizeId(id: string): string {
  const trimmed = id.trim();
  if (trimmed.length === 0) {
    return '';
  }
  return trimmed;
}

export function normalizeUserData(
  userData: UserForm
): UserForm & Pick<User, 'full_name'> {
  const normalizedFirstName = normalizeName(userData.first_name);
  const normalizedLastName = normalizeName(userData.last_name);

  const normalizedEmail = normalizeEmail(userData.email);

  const normalizedId = normalizeId(userData.client_user_id);

  const normalizedFullName = `${normalizedFirstName} ${normalizedLastName}`;

  return {
    ...userData,
    first_name: normalizedFirstName,
    last_name: normalizedLastName,
    full_name: normalizedFullName,
    email: normalizedEmail,
    client_user_id: normalizedId,
  };
}

function* createUser(action: {
  payload: UserForm;
}): SagaGenerator<UserForm & Pick<User, 'full_name'>> {
  const normalizedUserData = normalizeUserData(action.payload);
  yield* call(usersApiClient.create, normalizedUserData);
  return normalizedUserData;
}

function* deleteUsers(action: { payload: string[] }): SagaGenerator<string[]> {
  const users = action.payload;
  yield* all(
    users.map(async (u) => {
      await usersApiClient.delete(u);
    })
  );
  return users;
}
function* resendSignInEmail(action: {
  payload: string;
}): SagaGenerator<string> {
  const email = action.payload;
  yield* call(usersApiClient.resendSignInEmail, email);
  return email;
}

export interface UsersState {
  users: User[];
  usersAdmin: UserAdmin[];
  actions: {
    users: AsyncActionState;
    usersAdmin: AsyncActionState;
    createUser: AsyncActionState;
    deleteUsers: AsyncActionState;
    resendSignInEmail: AsyncActionState;
  };
}

const initialState: UsersState = {
  users: [],
  usersAdmin: [],
  actions: {
    users: { status: 'uninitialized' },
    usersAdmin: { status: 'uninitialized' },
    createUser: { status: 'uninitialized' },
    deleteUsers: { status: 'uninitialized' },
    resendSignInEmail: { status: 'uninitialized' },
  },
};

const slice = setupSlice('users', initialState)
  .addAsyncSagas({
    users: invoke(usersApiClient.users),
    usersAdmin: invoke(usersApiClient.usersAdmin),
    createUser,
    deleteUsers,
    resendSignInEmail,
  })
  .addReducers({
    'users/fulfilled': (state, action) => {
      state.users = action.payload;
    },
    'usersAdmin/fulfilled': (state, action) => {
      state.usersAdmin = action.payload;
    },
  });

export const actions = slice.actions;
export default slice.addExtra(asyncActionStateMatchers(actions).all());
